<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'next/button/Button.vue';
import TeleportWithDirection from 'next/TeleportWithDirection.vue';
import BugReportsContent from './BugReportsContent.vue';

defineProps({ isCollapsed: { type: Boolean, default: false } });

const route = useRoute();
const accountId = computed(() => route.params.accountId);
const featureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const enabled = computed(() =>
  featureEnabled.value(accountId.value, FEATURE_FLAGS.BUG_REPORTS)
);
const dialog = ref(null);
const isOpen = ref(false);
const isRecording = ref(false);
const recordingChanged = value => {
  isRecording.value = value;
  if (value) dialog.value?.close();
  else if (isOpen.value && !dialog.value?.open) dialog.value?.showModal();
};
let backdropPointerDown = false;

const open = () => {
  backdropPointerDown = false;
  isOpen.value = true;
  dialog.value.showModal();
};
const close = () => {
  isOpen.value = false;
  isRecording.value = false;
  backdropPointerDown = false;
  dialog.value?.close();
};
const closeFromBackdrop = () => {
  if (backdropPointerDown) close();
  backdropPointerDown = false;
};
watch([accountId, enabled], close);
</script>

<template>
  <div v-if="enabled" class="w-full px-1">
    <Button
      icon="i-lucide-bug"
      variant="ghost"
      color="slate"
      :label="isCollapsed ? undefined : $t('BUG_REPORTS.launch')"
      :aria-label="$t('BUG_REPORTS.launch')"
      :title="$t('BUG_REPORTS.launch')"
      class="w-full"
      @click="open"
    />
    <TeleportWithDirection to="body">
      <dialog
        ref="dialog"
        aria-labelledby="bug-reports-dialog-title"
        class="w-full max-w-3xl max-h-[90dvh] overflow-y-auto rounded-xl bg-n-background p-6 text-n-slate-12 shadow-xl backdrop:bg-black/40 backdrop:backdrop-blur-sm"
        @cancel.prevent="close"
        @close="!$event.target.open && !isRecording && (isOpen = false)"
        @pointerdown.capture="backdropPointerDown = $event.target === dialog"
        @pointercancel="backdropPointerDown = false"
        @click.self="closeFromBackdrop"
      >
        <header class="mb-6 flex items-center justify-between gap-4">
          <h2 id="bug-reports-dialog-title" class="text-xl font-semibold">
            {{ $t('BUG_REPORTS.title') }}
          </h2>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            :aria-label="$t('BUG_REPORTS.close')"
            :title="$t('BUG_REPORTS.close')"
            @click="close"
          />
        </header>
        <BugReportsContent
          v-if="isOpen"
          :key="accountId"
          @recording="recordingChanged"
        />
      </dialog>
    </TeleportWithDirection>
  </div>
</template>
